import { useEffect, useRef } from 'react';
import anime from 'animejs';
import { Scene3D } from '../3D/Scene3D';
import type { CVConfig } from '../../config/cvConfig';

interface HeroProps {
  config: CVConfig['personal'];
  theme: CVConfig['theme'];
}

export function Hero({ config, theme }: HeroProps) {
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // Animate hero elements on mount (no timeline)
    const easing = 'easeOutExpo';

    anime({
      targets: '.hero-title',
      translateY: [100, 0],
      opacity: [0, 1],
      duration: 1200,
      delay: 500,
      easing,
    });

    anime({
      targets: '.hero-subtitle',
      translateY: [50, 0],
      opacity: [0, 1],
      duration: 1000,
      delay: 900,
      easing,
    });

    anime({
      targets: '.hero-tagline',
      translateY: [30, 0],
      opacity: [0, 1],
      duration: 800,
      delay: 1000,
      easing,
    });

    anime({
      targets: '.hero-cta',
      scale: [0.8, 1],
      opacity: [0, 1],
      duration: 600,
      delay: 1100,
      easing,
    });

    // Glitch effect for title
    if (titleRef.current) {
      titleRef.current.setAttribute('data-text', config.name);
    }

    // Floating animation for CTA button
    anime({
      targets: '.hero-cta',
      translateY: [-5, 5],
      duration: 2000,
      direction: 'alternate',
      loop: true,
      easing: 'easeInOutSine'
    });

  }, [config.name]);

  const scrollToNext = () => {
    const nextSection = document.getElementById('about');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      id="hero"
    >
      {/* 3D Background */}
      <div className="absolute inset-0 z-0">
        <Scene3D 
          enableParticles={theme.enableParticles}
          enableRotation={theme.enable3D}
          className="w-full h-full"
        />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="space-y-8">
          {/* Main Title */}
          <h1 
            ref={titleRef}
            className="hero-title text-4xl sm:text-6xl lg:text-8xl font-orbitron font-black leading-tight glitch neon-text"
            data-text={config.name}
          >
            {config.name}
          </h1>

          {/* Subtitle */}
          <h2 className="hero-subtitle text-xl sm:text-2xl lg:text-4xl font-rajdhani font-light text-white/90 tracking-wide">
            {config.title}
          </h2>

          {/* Tagline */}
          <p className="hero-tagline text-lg sm:text-xl lg:text-2xl font-rajdhani text-[var(--synthwave-neon)] max-w-3xl mx-auto leading-relaxed">
            {config.tagline}
          </p>

          {/* Social Links */}
          <div className="flex justify-center space-x-6 mt-8">
            {config.socialLinks.github && (
              <a
                href={config.socialLinks.github}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link p-3 rounded-full neon-border hover:bg-[var(--synthwave-primary)]/10 transition-all duration-300 group"
              >
                <svg className="w-6 h-6 text-white group-hover:text-[var(--synthwave-neon)] transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
            )}
            
            {config.socialLinks.linkedin && (
              <a
                href={config.socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link p-3 rounded-full neon-border hover:bg-[var(--synthwave-primary)]/10 transition-all duration-300 group"
              >
                <svg className="w-6 h-6 text-white group-hover:text-[var(--synthwave-neon)] transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            )}

            {config.socialLinks.twitter && (
              <a
                href={config.socialLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link p-3 rounded-full neon-border hover:bg-[var(--synthwave-primary)]/10 transition-all duration-300 group"
              >
                <svg className="w-6 h-6 text-white group-hover:text-[var(--synthwave-neon)] transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
            )}

            {config.socialLinks.website && (
              <a
                href={config.socialLinks.website}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link p-3 rounded-full neon-border hover:bg-[var(--synthwave-primary)]/10 transition-all duration-300 group"
              >
                <svg className="w-6 h-6 text-white group-hover:text-[var(--synthwave-neon)] transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </a>
            )}
          </div>

          {/* CTA Button */}
          <div className="mt-12">
            <button
              onClick={scrollToNext}
              className="hero-cta inline-flex items-center px-8 py-4 text-lg font-rajdhani font-semibold text-black bg-[var(--synthwave-neon)] rounded-full hover:bg-white transition-all duration-300 transform hover:scale-105 neon-border"
            >
              Explore My Work
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-[var(--synthwave-neon)] rounded-full flex justify-center">
          <div className="w-1 h-3 bg-[var(--synthwave-neon)] rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
}